import { Component, OnDestroy, OnInit } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { InstanceRights, RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { takeUntil } from 'rxjs/operators';
import { AnnualDetailsNavigationEventService } from '../../Annual-Details/annual-details-event.service';
import { EntityService } from '../entity.service';
import { ClientServiceResponsibilityService } from '../../Client-Services/clientServiceResponsibility.service';
import { UIRouter } from '@uirouter/angular';
import { ParcelService } from './parcel.service.upgrade';
import { StateService } from '../../Common/States/States.Service';
import { SiteService } from '../Site/Site.Service.upgrade';
import { InstanceRepository } from '../Instance/instance.repository';
import { StateSummary } from '../../Common/States/state.model';
import { ToastrService } from 'ngx-toastr';
import { UtilitiesService } from '../../UI-Lib/Utilities';
import { Constants } from '../../constants.new';
import { AceLoaderComponent } from '../../UI-Lib/ACE/Loader/loader.component';
import { CommonModule } from '@angular/common';
import { ParcelInfoPanelComponent } from './Info/parcelInfoPanel.component';
import { ParcelActivitySummaryComponent } from './Activity/parcel-activity-summary.component';
import { AnnualDetailsComponent } from '../../Annual-Details/annual-details.component';
import { EntityPropertyCharacteristicsComponent } from '../../Property-Characteristics/Entity-Property-Characteristics/entity.property.characteristics.component';
import { ClientServicesPanelComponent } from '../../Client-Services/Panel/clientServicesPanel.component';
import { AssetListPanelComponent } from '../Site/Assets/assetListPanel.component';
import { ResponsibleEntityPanelComponent } from '../Site/Responsible-Entity/responsibleEntityPanel.component';
import { ComplianceParcelModule } from '../../Compliance/Parcel/parcel.module';
import { ContactPanelComponent } from '../../Contact/Panel/contactPanel.component';
import { AccrualsPanelComponent } from './Accruals/accrualsPanel.component';
import { BreadCrumb, BreadCrumbsComponent } from '../../UI-Lib/Bread-Crumb/breadCrumbs.component';

import moment from 'moment';

interface ParcelEntity {
    id: number;
    name: string;
    type: string;
    typeId: number;
    newAllowed: boolean;
    canEditCompanySetups: boolean;
    hasWritePermission: boolean;
    isInactive: boolean;
}

interface ParcelAssessmentClassComponent extends Weissman.Model.Assessments.AssessmentClassComponent {
    componentName: string;
}

@Component({
    templateUrl: 'parcel.component.html',
    imports: [
        CommonModule,
        AceLoaderComponent,
        ParcelInfoPanelComponent,
        ParcelActivitySummaryComponent,
        AnnualDetailsComponent,
        EntityPropertyCharacteristicsComponent,
        ClientServicesPanelComponent,
        AssetListPanelComponent,
        ResponsibleEntityPanelComponent,
        ComplianceParcelModule,
        ContactPanelComponent,
        AccrualsPanelComponent,
        BreadCrumbsComponent,
    ],
})
export class ParcelComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _annualDetailsNavigationEventService: AnnualDetailsNavigationEventService,
        private readonly _entityService: EntityService,
        private readonly _csrService: ClientServiceResponsibilityService,
        private readonly _restrictService: RestrictService,
        private readonly _parcelService: ParcelService,
        private readonly _statesService: StateService,
        private readonly _siteService: SiteService,
        private readonly _instanceRepository: InstanceRepository,
        private readonly _constants: Constants,
        private readonly _toastr: ToastrService,
        private readonly _router: UIRouter,
        public readonly utils: UtilitiesService,
    ) {}

    parcel: Weissman.Model.Parcels.Parcel;
    parcelViewDTO: Core.ParcelViewDTO;
    entity: ParcelEntity;
    states: StateSummary[];
    compTypes: ParcelAssessmentClassComponent[];
    annualYearData: Core.AnnualGridYearsInfo;
    breadcrumbs: BreadCrumb[];

    loadingData = true;
    ppReturnPreparationAllowed: boolean;
    hasPrivateItemsPermission: boolean;
    showComplianceInfo: boolean;
    stateAllowConsolidating: boolean;
    enableAllocations: boolean;
    hasWritePermission: boolean;
    hasComplianceResponsibility: boolean;

    topLevelCompanyId: number;
    parcelIds: number[];
    instanceId: number;
    parcelId: number;
    siteId: number;
    companyId: number;
    prevParcelId: number;
    nextParcelId: number;
    years: any[];

    panelMaximized: string;
    maximize: boolean;

    private _destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        this._annualDetailsNavigationEventService.resetAnnualDetails();

        const { siteId, companyId, parcelId } = this._router.globals.params;
        this.siteId = +siteId;
        this.companyId = +companyId;
        this.parcelId = parseInt(parcelId, 10);

        this._entityService.maximizeChange$.pipe(takeUntil(this._destroy$)).subscribe((changeData) => {
            if (changeData) {
                this.panelMaximized = changeData.maximized ? changeData.panelName : null;
            }
        });

        this._csrService.clientServicesChange$.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this._csrService.getHasComplianceResponsibility(this.parcelId, 6).then((result) => {
                this.hasComplianceResponsibility = result;
            });
        });

        const calls = [
            this._parcelService.getView(parcelId, true),
            this._statesService.getSummary(),
            this._csrService.getHasComplianceResponsibility(parcelId, 6),
            this._doPermissionCheck(),
            lastValueFrom(this._instanceRepository.getEntityInstanceId('parcel', parcelId)),
        ];

        const companyIDErrMsg = 'Company ID does not match Parcel';
        const siteIDErrMsg = 'Site ID does not match Parcel';
        const notFoundErrMsg = 'Parcel not found';
        // After all requests return, this runs setting up various entities

        try {
            const [parcelView, stateSummary, hasCompliance, permissions, instanceId] = await Promise.all(calls);

            const parcelCompanyId = parcelView.companyId;
            const parcelSiteId = parcelView.siteId;

            if (parcelCompanyId != this.companyId) {
                throw new Error(companyIDErrMsg);
            }
            if (parcelSiteId != this.siteId) {
                throw new Error(siteIDErrMsg);
            }

            this.ppReturnPreparationAllowed = parcelView.ppReturnPreparationAllowed;
            this.topLevelCompanyId = parcelView.topLevelCompanyId;
            this.parcel = parcelView.parcel;
            this.parcelViewDTO = parcelView;

            this.breadcrumbs = [
                {
                    name: parcelView.companyName,
                    target: 'company',
                    options: { companyId: companyId },
                },
                {
                    name: parcelView.siteName,
                    target: 'site',
                    options: { companyId: companyId, siteId: siteId },
                },
            ];

            this.hasPrivateItemsPermission =
                this._restrictService.isInRole(Roles.RYANPRIVATEITEMSVIEW) ||
                this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT);

            this.showComplianceInfo =
                parcelView.ppReturnPreparationAllowed &&
                this._restrictService.hasInstanceRight(
                    InstanceRights.COMPLIANCEFEATURESVIEW,
                    this.parcel.site.company.instanceID,
                ) &&
                parcelView.parcel.propertyTypeID === this._constants.PropertyTypes.PP.propertyTypeID;

            this.entity = {
                id: this.parcel.parcelID,
                name: this.parcel.acctNum,
                // ToDo: Create a global entity enum that encapsulates type and typeid?
                type: 'parcel',
                typeId: 6,
                newAllowed: false,
                canEditCompanySetups: this.parcel.canEditCompanySetups,
                hasWritePermission: permissions,
                isInactive: this.parcel.activityStatusID === 0,
            };

            const storedExcludeInactiveStr = localStorage.getItem('ws_siteExcludeInactive' + siteId); // Will be '0' or '1' or null if not set
            const storedExcludeInactive = storedExcludeInactiveStr ? !!Number(storedExcludeInactiveStr) : true;
            const excludeInactive = this.entity.isInactive ? false : storedExcludeInactive; // if the current parcel is inactive, we need to include inactive parcels in the list in order to find the current parcel id and avoid an error

            this.parcelIds = await this._siteService.getParcelIdsBySiteId(siteId, excludeInactive);

            this.compTypes = this._getCompTypes();
            this.states = stateSummary;
            const parcelState = this.states.find((s) => s.stateID === parcelView.parcel.address.stateID);
            this.stateAllowConsolidating = parcelState && parcelState.allowConsolidating;
            this.hasComplianceResponsibility = hasCompliance;
            this.instanceId = instanceId;
            this.enableAllocations = parcelView.parcel.enableAllocations;
            this.loadingData = false;

            this._identifyPrevAndNextParcelsIds(this.parcelIds);

            // No need to do anything with the result; this causes site data to be cached
            await this._siteService.load(siteId, companyId);

            await this._getAnnualGrid();
        } catch (err) {
            if ([companyIDErrMsg, siteIDErrMsg, notFoundErrMsg].indexOf(err.message) >= 0) {
                this._toastr.error(err.message);
            }
            this.loadingData = false;
            throw err;
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    maximizeChanged(targetPanelName: string, maximized: boolean): void {
        this.panelMaximized = maximized ? targetPanelName : null;
    }

    openParcel(parcelId: number): void {
        if (!parcelId) {
            return;
        }

        this._router.stateService.go('parcel', {
            companyId: this.companyId,
            siteId: this.siteId,
            parcelId: parcelId,
        });
    }

    parcelInfoChanged(parcel: Weissman.Model.Parcels.Parcel): void {
        this.parcel.parcelAssessmentClasses = parcel.parcelAssessmentClasses;
    }

    async getAnnualGridAndCompTypes(getCompTypesOnly: boolean): Promise<void> {
        //getting parcel here when we don't need to. Already fresh on info panel.
        //need refactor.
        this.parcel = await this._parcelService.get({
            siteId: this.siteId,
            parcelId: this.parcelId,
        });

        this.compTypes = this._getCompTypes();

        if (!getCompTypesOnly) {
            await this._getAnnualGrid();
            this.hasComplianceResponsibility = await this._csrService.getHasComplianceResponsibility(this.parcelId, 6);
        }
    }

    private _identifyPrevAndNextParcelsIds(parcelIds: number[]): void {
        if (parcelIds.length < 2)
            // we have no more parcels
            return;

        const i = parcelIds.indexOf(this.parcelId);
        if (i < 0)
            // this parcel id can't be found
            throw 'identifyPrevAndNextParcelsIds - unable to find current parcel id in the parcelIds array';

        const prevIndex = i - 1;
        const nextIndex = i + 1;

        if (prevIndex >= 0) this.prevParcelId = parcelIds[prevIndex];

        if (nextIndex < parcelIds.length) this.nextParcelId = parcelIds[nextIndex];
    }

    // Returns an array of unique component types from the parcel's asssessment class(es)
    // Note that this same logic is needed for document processing, but it is done in the
    // backend. If this code needs to be changed, please make sure to also review the API
    // call to /api/annualassessmentview/assessment/[assessmentID]. As of this writing,
    // that code is at line 193 of roject_weissman\weissman.dataaccesslayer\assessment\annualyeardal.cs
    private _getCompTypes(): ParcelAssessmentClassComponent[] {
        return this.parcel.parcelAssessmentClasses.reduce((acc, aClass) => {
            const uniqueComps = aClass.assessmentClass.assessmentClassComponents
                .filter((x) => x.propertyTypeID === this.parcel.propertyTypeID)
                .reduce(
                    (acc2, x) => {
                        let componentName = x.assessmentComponent.componentName;
                        if (this.parcel.parcelAssessmentClasses.length > 1) {
                            componentName = `${aClass.assessmentClass.name} - ${componentName}`;
                        }

                        acc2[x.assessmentComponentID] = { ...x, componentName };
                        return acc2;
                    },
                    {} as { [id: number]: ParcelAssessmentClassComponent },
                );

            return [...acc, ...Object.values(uniqueComps)];
        }, []);
    }

    private async _doPermissionCheck(): Promise<boolean> {
        const restrictionData = {
            isCompany: false,
            entityId: this.siteId,
            roles: [],
            flag: Core.AccessRightsEnum.Write,
        };

        this.hasWritePermission = await this._restrictService.hasPermission(restrictionData);
        return this.hasWritePermission;
    }

    // Gets annual grid data
    private async _getAnnualGrid(): Promise<void> {
        this.years = undefined;

        const years = await this._parcelService.getAnnualAssessmentsView(this.parcelId);

        this.annualYearData = years;

        this.years = years.years.map((year) => {
            year.collapsedAppealStatus = Date.parse(year.collapsedAppealStatus)
                ? moment.utc(year.collapsedAppealStatus).format('M/D/YYYY')
                : year.collapsedAppealStatus;

            year.annualGridDetails = year.annualGridDetails.map((revision) => {
                revision.appealStatus = Date.parse(revision.appealStatus)
                    ? moment.utc(revision.appealStatus).format('M/D/YYYY')
                    : revision.appealStatus;
                return revision;
            });

            return year;
        });
    }
}
